/**
 * Read adapters for stored choice payloads (plots plan J; contract in
 * `../contract.ts`, shapes in docs/deck-design-system/plots/read-adapters.md).
 *
 * Shape only: they rename and regroup what Python already stamped, and never
 * compute, round, re-sort, roll up or crown. A payload built since J carries
 * its `plot` (or `choice_plot`) and is read as is. The legacy readers keep
 * today's rules for stored payloads that predate the stamp:
 * - a bars slide stored without `plot`: `value` is the share, `isTop` the
 *   winner, `breakdown` the Other members; today's figure rule (whole numbers
 *   whole, else one decimal) because no `decimals` was stamped; every row an
 *   option (the stored Other row carries no typed role, and its label is
 *   never matched);
 * - a summary distribution card stored without `plot`: its rows as stamped,
 *   no winner (the card never highlighted one), today's card rounding.
 * Stored shapes: `tests/fixtures/plots/choice-bars-{slide,card}.stored.v1.json`.
 */

import { legacyCardShareFor, legacyChoiceShare } from './format'

import { ChoiceBarsItem, ChoiceBarsLabels } from '@digital-twins/slide-schemas/generated/plot-types'
import { PlotReadAdapter, StoredPlotPayload } from '../contract'

export type ChoiceSlideType = 'single-select-bars' | 'multi-select-bars'

/** A deck single/multi-select bars slide as stored (`*-select-bars.schema.json`), plot optional. */
export interface StoredChoiceSlide {
  type: ChoiceSlideType
  bars: Array<{
    id: string
    label: string
    value: number
    count?: number | null
    isTop?: boolean
    breakdown?: Array<{ label: string; value: number }> | null
    imageUrl?: string | null
  }>
  displayLabels?: { multiSelectNote?: string | null } | null
  plot?: { kind: string; data: unknown } | null
}

/** The summary card `distribution` payload as stored (`DistributionPayload`), plot optional. */
export interface StoredDistributionCard {
  shape: 'distribution'
  rows: Array<{ id: string; label: string; share: number; count?: number | null }>
  baseSize?: number | null
  unit?: string | null
  unitLabel?: string | null
  plot?: { kind: string; data: unknown; alternatives?: unknown } | null
}

/** What a read returns: the payload, whether Python stamped it, and the legacy figure rule. */
export interface ChoiceRead {
  payload: StoredPlotPayload<'choice-bars'>
  isStamped: boolean
  /** Set only by a legacy reader: today's figure string for a payload with no stamped decimals. */
  formatShare?: (share: number) => string
  /** The stamped share-donut alternative, when the producer offered the toggle. */
  alternatives?: unknown
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** A stamped `choice-bars` plot, by the fields present (never by what a value means). */
export function isStampedChoicePlot(plot: unknown): plot is StoredPlotPayload<'choice-bars'> & { alternatives?: unknown } {
  return isRecord(plot) && plot.kind === 'choice-bars' && isRecord(plot.data) && Array.isArray(plot.data.series)
}

/** A deck bars slide stored before `plot`: today's reader, moved here. */
export const legacyDeckChoiceSlideAdapter: PlotReadAdapter<'choice-bars', StoredChoiceSlide> = {
  kind: 'choice-bars',
  source: { surface: 'deck-slide', shape: 'single-select-bars' },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredChoiceSlide {
    return (
      isRecord(stored) &&
      (stored.type === 'single-select-bars' || stored.type === 'multi-select-bars') &&
      Array.isArray(stored.bars)
    )
  },
  read(stored: StoredChoiceSlide): StoredPlotPayload<'choice-bars'> {
    const items: Array<ChoiceBarsItem> = stored.bars.map((bar) => ({
      id: bar.id,
      label: bar.label,
      share: bar.value,
      count: bar.count ?? null,
      role: 'option',
      colorSlot: null,
      imageUrl: bar.imageUrl ?? null,
      ...(bar.breakdown?.length
        ? { otherMembers: bar.breakdown.map((member) => ({ id: member.label, label: member.label, share: member.value })) }
        : {}),
    }))
    const topIds = stored.bars.filter((bar) => bar.isTop === true).map((bar) => bar.id)
    const labels: Partial<ChoiceBarsLabels> = stored.displayLabels?.multiSelectNote?.trim()
      ? { multi_select_note: stored.displayLabels.multiSelectNote }
      : {}
    return {
      kind: 'choice-bars',
      data: {
        basis: 'respondents',
        multiSelect: stored.type === 'multi-select-bars',
        series: [{ id: 'total', label: null, base: null, lowBase: false, items, topIds, tie: topIds.length > 1 }],
        // Unused: the legacy figure rule prints stored values (`legacyChoiceShare`).
        decimals: 1,
        labels,
      },
    }
  },
}

/** A summary distribution card stored before `plot`: its rows as stamped. */
export const legacyDistributionCardAdapter: PlotReadAdapter<'choice-bars', StoredDistributionCard> = {
  kind: 'choice-bars',
  source: { surface: 'summary-card', shape: 'distribution' },
  isLegacy: true,
  accepts(stored: unknown): stored is StoredDistributionCard {
    return isRecord(stored) && stored.shape === 'distribution' && Array.isArray(stored.rows)
  },
  read(stored: StoredDistributionCard): StoredPlotPayload<'choice-bars'> {
    return {
      kind: 'choice-bars',
      data: {
        basis: 'respondents',
        multiSelect: false,
        series: [
          {
            id: 'total',
            label: null,
            base: stored.baseSize ?? null,
            lowBase: false,
            items: stored.rows.map((row) => ({
              id: row.id,
              label: row.label,
              share: row.share,
              count: row.count ?? null,
              role: 'option',
              colorSlot: null,
            })),
            // The card never highlighted a winner; nothing is inferred.
            topIds: [],
            tie: false,
          },
        ],
        decimals: 1,
        labels: {},
      },
    }
  },
}

/** A deck bars slide through its stamped plot, else the legacy reader. */
export function readChoiceSlide(slide: StoredChoiceSlide): ChoiceRead {
  if (isStampedChoicePlot(slide.plot)) {
    return { payload: slide.plot, isStamped: true }
  }
  return { payload: legacyDeckChoiceSlideAdapter.read(slide), isStamped: false, formatShare: legacyChoiceShare }
}

/** A summary distribution card through its stamped plot, else the legacy reader. */
export function readDistributionCard(card: StoredDistributionCard): ChoiceRead {
  if (isStampedChoicePlot(card.plot)) {
    return { payload: card.plot, isStamped: true, alternatives: card.plot.alternatives }
  }
  return { payload: legacyDistributionCardAdapter.read(card), isStamped: false, formatShare: legacyCardShareFor(card.unit) }
}

/** A quick-study result carrying Python's `choice_plot` (plots plan J); stored results do not. */
export interface QuickStudyChoiceResult {
  choice_plot?: unknown
}

/** The quick-study `choice_plot`, or null (the stored result keeps the browser chart). */
export function readQuickStudyChoice(result: QuickStudyChoiceResult): ChoiceRead | null {
  const plot = result.choice_plot
  if (!isStampedChoicePlot(plot)) {
    return null
  }
  return { payload: plot, isStamped: true, alternatives: plot.alternatives }
}
